// src/components/AuthModal.js
// Modal overlay para Login/Registro con tabs
import React, { useState, useEffect, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService, API_BASE_URL, BACKEND_BASE } from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import { useIdioma } from '../contexts/IdiomaContext';
import { usePais } from '../contexts/PaisContext';
import { normalizarPais } from '../utils/pais';
import { buscarCodigoPais, OPCIONES_CODIGO_COMPLETAS } from '../utils/countryCodes';
import { normalizarCampos, listaValores, alternarValor, cajaCasillas } from '../utils/registroCampos';
import axios from 'axios';
import VerContrasena from './VerContrasena';

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// Entrada «de salto» (crece desde el centro con un leve rebote), opcional por proyecto.
const popIn = keyframes`
  0% { opacity: 0; transform: scale(0); }
  70% { opacity: 1; transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1); }
`;

// Styled components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => p.$overlay || 'rgba(0, 0, 0, 0.7)'};
  backdrop-filter: ${p => p.$blur || 'blur(8px)'};
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.25s ease-out;
`;

// Modo embebido: el mismo formulario, pero como bloque dentro de una página
// (la "página de registro independiente") en vez de modal sobre la landing.
const EmbedWrap = styled.div`
  display: flex;
  justify-content: center;
  /* La página que lo embebe puede quitar este aire (p. ej. cuando el formulario
     ya vive dentro de una tarjeta con su propio padding). */
  padding: ${p => p.$padding || '48px 20px'};
  animation: ${fadeIn} 0.25s ease-out;
`;

const ModalCard = styled.div`
  background: ${p => p.$bg || '#fff'};
  color: ${p => p.$color || 'inherit'};
  font-family: ${p => p.$font || 'inherit'};
  border-radius: ${p => p.$radio || '20px'};
  box-shadow: ${p => p.$sombra || '0 25px 60px rgba(0, 0, 0, 0.3)'};
  ${p => p.$blur && `backdrop-filter: ${p.$blur};`}
  ${p => p.$borde && `border: ${p.$borde};`}
  width: 100%;
  max-width: ${p => p.$maxWidth || '420px'};
  max-height: ${p => p.$embedded ? 'none' : 'calc(100dvh - 40px)'};
  overflow-y: ${p => p.$embedded ? 'visible' : 'auto'};
  animation: ${p => p.$rebote ? css`${popIn} 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)` : css`${slideUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1)`};
  @media (prefers-reduced-motion: reduce) { animation: none; }
  position: relative;

  /* Scrollbar fina y estilizada — colores configurables desde el admin */
  scrollbar-width: thin;
  scrollbar-color: ${p => p.$scrollThumb || '#cbd5e1'} ${p => p.$scrollTrack || 'transparent'};
  &::-webkit-scrollbar {
    width: ${p => p.$scrollWidth || '6px'};
  }
  &::-webkit-scrollbar-track {
    background: ${p => p.$scrollTrack || 'transparent'};
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.$scrollThumb || '#cbd5e1'};
    border-radius: 999px;
    transition: background 0.2s;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${p => p.$scrollThumbHover || p.$scrollThumb || '#94a3b8'};
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: ${p => p.$top || '14px'};
  right: ${p => p.$right || '14px'};
  width: ${p => p.$size || '32px'};
  height: ${p => p.$size || '32px'};
  border-radius: 50%;
  border: ${p => p.$borde || 'none'};
  background: ${p => p.$bg || 'rgba(0, 0, 0, 0.06)'};
  color: ${p => p.$color || '#64748B'};
  font-size: ${p => p.$fontSize || '1.2rem'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  /* Por encima de la barra de título, que es sticky con z-index 2. */
  z-index: 3;

  &:hover {
    background: ${p => p.$bgHover || 'rgba(0, 0, 0, 0.12)'};
    color: ${p => p.$colorHover || p.$color || '#1e293b'};
    ${p => p.$girar && 'transform: rotate(90deg) scale(1.1); box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);'}
  }
  ${p => p.$movil && `@media (max-width: ${p.$movil.bp}) {
    ${p.$movil.size ? `width: ${p.$movil.size}; height: ${p.$movil.size};` : ''}
    ${p.$movil.top ? `top: ${p.$movil.top}; right: ${p.$movil.top};` : ''}
  }`}
`;

/* Encabezado en barra de color (título + subtítulo sobre un degradado), el
   diseño de Uroacademy. Se activa con `encabezado_barra`; sin esa clave el
   modal sigue mostrando el logo del proyecto como siempre. */
const HeaderBar = styled.div`
  background: ${p => p.$bg || 'linear-gradient(135deg, #1b3d5e 0%, #458cd3 100%)'};
  color: ${p => p.$color || '#fff'};
  padding: ${p => p.$padding || '1.75rem 3rem'};
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 2;
  ${p => p.$paddingMovil && `@media (max-width: ${p.$bp}) { padding: ${p.$paddingMovil}; }`}
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: ${p => p.$size || '2rem'};
  font-weight: ${p => p.$peso || 800};
  ${p => p.$fuente && `font-family: ${p.$fuente};`}
  line-height: 1.2;
  ${p => p.$sombra && `text-shadow: ${p.$sombra};`}
  @media (max-width: ${p => p.$bp || '600px'}) { font-size: ${p => p.$sizeMobile || '1.5rem'}; }
`;

const HeaderSub = styled.p`
  margin: ${p => p.$titulo ? '0.6rem 0 0' : '0'};
  font-size: ${p => p.$size || '1rem'};
  line-height: 1.45;
  opacity: ${p => p.$opacidad || 0.95};
  ${p => p.$sizeMobile && `@media (max-width: ${p.$bp || '600px'}) { font-size: ${p.$sizeMobile}; line-height: 1.3; margin-top: 0; }`}
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 0;
  text-align: center;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-bottom: 1.2rem;
`;

const LogoText = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${p => p.$color || '#0f355b'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
`;

const TabRow = styled.div`
  display: flex;
  background: ${p => p.$bg || '#f1f5f9'};
  border-radius: 12px;
  padding: 4px;
  margin: 0 2rem;
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.6rem;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  background: ${p => p.$active ? (p.$bgActive || '#fff') : 'transparent'};
  color: ${p => p.$active ? (p.$colorActive || '#0f355b') : (p.$colorInactive || '#94A3B8')};
  box-shadow: ${p => p.$active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'};
`;

const FormBody = styled.div`
  /* El proyecto puede darle otro aire a la tarjeta (padding_formulario), que
     es lo que necesita una página de registro con su propio diseño. */
  padding: ${p => p.$padding || '1.5rem 2rem 2rem'};
  ${p => p.$fondo && `background: ${p.$fondo};`}
  ${p => p.$paddingMovil && `@media (max-width: 768px) { padding: ${p.$paddingMovil}; }`}
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const InputLabel = styled.label`
  display: block;
  font-size: ${p => p.$size || '0.85rem'};
  font-weight: 600;
  color: ${p => p.$color || '#334155'};
  margin-bottom: ${p => p.$margen || '0.35rem'};
  ${p => p.$espaciado && `letter-spacing: ${p.$espaciado};`}
`;

const InputBox = styled.div`
  position: relative;
  display: flex; align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 0.85rem; pointer-events: none;
  font-size: 1rem;
  opacity: 0.85;
`;

const Input = styled.input`
  width: 100%;
  padding: ${p => p.$padding || '0.7rem 1rem'};
  ${p => p.$hasIcon && 'padding-left: 2.3rem;'}
  border-radius: ${p => p.$radio || '10px'};
  border: ${p => p.$borderWidth || '1.5px'} solid ${p => p.$borderColor || '#e2e8f0'};
  font-size: ${p => p.$fontSize || '0.95rem'};
  ${p => p.$colorTexto && `color: ${p.$colorTexto};`}
  background: ${p => p.$bg || '#f8fafc'};
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  ${p => p.$colorPlaceholder && `&::placeholder { color: ${p.$colorPlaceholder}; opacity: 1; }`}

  &:focus {
    border-color: ${p => p.$borderColorFocus || '#0891B2'};
    background: #fff;
    box-shadow: ${p => p.$sombraFocus || `0 0 0 3px ${p.$focusGlow || 'rgba(8, 145, 178, 0.1)'}`};
    ${p => p.$sombraFocus && 'transform: translateY(-1px);'}
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: ${p => p.$radio || '10px'};
  border: ${p => p.$borderWidth || '1.5px'} solid ${p => p.$borderColor || '#e2e8f0'};
  font-size: 0.95rem;
  background: ${p => p.$bg || '#f8fafc'};
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    border-color: ${p => p.$borderColorFocus || '#0891B2'};
    background: #fff;
    box-shadow: 0 0 0 3px ${p => p.$focusGlow || 'rgba(8, 145, 178, 0.1)'};
  }
`;

const SubmitBtn = styled.button`
  /* Ancho, borde y alineación configurables: hay diseños donde el botón no es
     una barra a todo el ancho sino una píldora de contorno centrada. */
  width: ${p => p.$ancho || '100%'};
  ${p => p.$align === 'center' ? 'display: block; margin-left: auto; margin-right: auto;' : ''}
  ${p => p.$align === 'right' ? 'display: block; margin-left: auto;' : ''}
  padding: ${p => p.$padding || '0.75rem'};
  border: ${p => p.$borde || 'none'};
  border-radius: ${p => p.$radio || '10px'};
  font-size: ${p => p.$fontSize || '1rem'};
  font-weight: ${p => p.$peso || 700};
  color: ${p => p.$color || '#fff'};
  background: ${p => p.$bg || 'linear-gradient(135deg, #0f355b 0%, #1a4a7a 100%)'};
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: ${p => p.$margen || '0.5rem'};
  ${p => p.$sombra && `box-shadow: ${p.$sombra};`}
  ${p => p.$espaciado && `letter-spacing: ${p.$espaciado};`}

  &:hover {
    transform: translateY(-${p => (p.$sombra ? 2 : 1)}px);
    box-shadow: ${p => p.$sombraHover || '0 6px 20px rgba(15, 53, 91, 0.3)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

/* Aviso de error. Con `$caja` toma la forma del diseño: recuadro rojo suave
   al principio del formulario; sin ella, la línea roja de siempre. */
const ErrorMsg = styled.p`
  color: ${p => p.$color || '#ef4444'};
  font-size: ${p => p.$caja ? '0.95rem' : '0.85rem'};
  text-align: ${p => p.$caja ? 'left' : 'center'};
  margin: ${p => p.$margen || (p.$caja ? '0 0 1.25rem' : '0.5rem 0 0')};
  ${p => p.$tamano && `font-size: ${p.$tamano};`}
  ${p => p.$caja && `
    border: 1px solid ${p.$borde || '#e53e3e'};
    background: ${p.$bg || '#fff5f5'};
    border-radius: 8px;
    padding: ${p.$padding || '0.85rem 1rem'};
    line-height: 1.4;
  `}
`;

/* Error bajo el campo de correo (validación mientras se escribe). */
const CampoError = styled.div`
  color: ${p => p.$color || '#ef4444'};
  font-size: 12px;
  line-height: 1;
  margin-top: 4px;
`;

/* Aviso arriba del formulario de acceso: «Verificando», bienvenida o error. */
const AvisoAcceso = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  ${p => p.$tipo === 'exito' ? `
    background: linear-gradient(135deg, #f0fff4, #c6f6d5);
    color: #22543d;
    border: 1px solid #9ae6b4;
  ` : `
    background: linear-gradient(135deg, #fff5f5, #fed7d7);
    color: #c53030;
    border: 1px solid #feb2b2;
  `}
`;

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SwitchText = styled.p`
  text-align: center;
  font-size: 0.85rem;
  color: ${p => p.$color || '#64748B'};
  margin-top: 1rem;

  button {
    background: none;
    border: none;
    color: ${p => p.$linkColor || '#0891B2'};
    font-weight: 700;
    cursor: pointer;
    font-size: 0.85rem;
    text-decoration: underline;

    &:hover {
      opacity: 0.85;
    }
  }
`;

// Mini chip icon for modal logo
const MiniChip = () => (
  <svg width="28" height="28" viewBox="0 0 100 100" fill="none">
    <rect x="22" y="22" width="56" height="56" rx="12" stroke="#0f355b" strokeWidth="3" fill="rgba(15,53,91,0.06)"/>
    <line x1="38" y1="14" x2="38" y2="22" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="50" y1="14" x2="50" y2="22" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="62" y1="14" x2="62" y2="22" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="38" y1="78" x2="38" y2="86" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="50" y1="78" x2="50" y2="86" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="62" y1="78" x2="62" y2="86" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="14" y1="38" x2="22" y2="38" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="14" y1="50" x2="22" y2="50" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="14" y1="62" x2="22" y2="62" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="78" y1="38" x2="86" y2="38" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="78" y1="50" x2="86" y2="50" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="78" y1="62" x2="86" y2="62" stroke="#0891B2" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="50" cy="50" r="10" fill="rgba(8,145,178,0.15)"/>
    <circle cx="50" cy="50" r="5" fill="#0891B2"/>
  </svg>
);

const AuthModal = ({ isOpen, onClose = () => {}, initialTab = 'login', showLoginTab = true, showRegisterTab = true, embedded = false, embedPadding = null, configOverride = null, urlDestino = null }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { proyecto } = useProject();
  const { idioma } = useIdioma();
  // País del visitante: decide qué campos se piden y qué perfil se asigna.
  const { audiencia } = usePais();
  // Personalización del modal (definida por proyecto.config_json.auth_modal).
  // `configOverride` deja que una página lo re-estilice sin tocar el modal de la
  // landing (lo usa la página de registro independiente).
  const cfg = { ...(proyecto?.config_json?.auth_modal || {}), ...(configOverride || {}) };

  // El proyecto puede apagar una pestaña para todo el sitio (por ejemplo, cuando
  // el registro vive en su propia página y no debe ofrecerse desde el modal).
  const tabLogin = showLoginTab && cfg.ocultar_tab_login !== true;
  const tabRegistro = showRegisterTab && cfg.ocultar_tab_registro !== true;
  const showIcons = !!cfg.mostrar_iconos;
  // A dónde se va la persona al entrar (y al registrarse, si no se pide otra
  // cosa). Sin configurar, al centro de contenidos como siempre.
  // Quien abre el modal puede pedir otro destino (p. ej. el curso del botón «Acceder»).
  const destinoTrasEntrar = urlDestino || cfg.url_destino || '/contenidos';
  const [tab, setTab] = useState(initialTab);

  // Resolver textos multi-idioma: string -> tal cual; objeto {es,en,pt} -> por idioma
  const tl = (value, fallback = '') => {
    if (value == null || value === '') return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value[idioma] || value['es'] || fallback;
    return fallback;
  };

  // Placeholders: el proyecto puede ocultarlos (diseños donde la etiqueta
  // arriba del campo ya alcanza y el texto de ejemplo ensucia).
  const ph = (valor) => (cfg.ocultar_placeholders ? '' : valor);

  // Estado de éxito post-registro (cuando sin_auto_login está activo)
  const [regSuccess, setRegSuccess] = useState(false);

  // Reabrir en el tab pedido cuando cambia initialTab (ej. flujo idioma-primero del hero)
  // Si el tab pedido está oculto, caer al que sí esté visible.
  useEffect(() => {
    if (!isOpen) {
      setRegSuccess(false);
      setLoginAviso(null);
      setEmailTocado(false);
      setVerificando(false);
      return;
    }
    if (initialTab === 'login' && !tabLogin && tabRegistro) {
      setTab('register');
    } else if (initialTab === 'register' && !tabRegistro && tabLogin) {
      setTab('login');
    } else {
      setTab(initialTab);
    }
  }, [isOpen, initialTab, tabLogin, tabRegistro]);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  // Validación del correo mientras se escribe y aviso del ingreso (opcionales).
  const [emailTocado, setEmailTocado] = useState(false);
  const [loginAviso, setLoginAviso] = useState(null); // { tipo: 'exito' | 'error', texto }
  const [verificando, setVerificando] = useState(false);

  // Register state
  const [regNombre, setRegNombre] = useState('');
  const [regApellido, setRegApellido] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regRole, setRegRole] = useState('');
  const [regError, setRegError] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // Campos dinámicos de registro (Fase 12b)
  const [registroConfig, setRegistroConfig] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [regConvenioId, setRegConvenioId] = useState('');
  const [regTelefono, setRegTelefono] = useState('');
  const [regCodigoPais, setRegCodigoPais] = useState('+57');
  // Opciones de código telefónico derivadas de los países configurados del
  // proyecto (mapeados a su dial). Si no hay países definidos, lista completa.
  const codigosTelefono = useMemo(() => {
    const paisesCfg = registroConfig?.paises || [];
    if (paisesCfg.length) {
      return paisesCfg
        .map(p => {
          const info = buscarCodigoPais(p);
          return info ? { value: info.dial, label: `${info.dial} ${info.iso}`, key: p } : null;
        })
        .filter(Boolean);
    }
    return OPCIONES_CODIGO_COMPLETAS.map(p => ({ value: p.dial, label: `${p.dial} ${p.iso} · ${p.nombre}`, key: p.iso }));
  }, [registroConfig?.paises]);
  // Si el código por defecto no está entre las opciones disponibles, usar el primero.
  useEffect(() => {
    if (codigosTelefono.length && !codigosTelefono.some(o => o.value === regCodigoPais)) {
      setRegCodigoPais(codigosTelefono[0].value);
    }
  }, [codigosTelefono]); // eslint-disable-line react-hooks/exhaustive-deps
  const [regEspecialidad, setRegEspecialidad] = useState('');
  const [regPais, setRegPais] = useState('');
  const [regDni, setRegDni] = useState('');
  const [regSubrol, setRegSubrol] = useState('');
  // Valores de campos personalizados agregados desde el editor del proyecto.
  const [regCustomValues, setRegCustomValues] = useState({});
  // Checks de aceptación marcados por el usuario {key: true}
  const [regChecks, setRegChecks] = useState({});

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isOpen) {
      const fetchGroups = async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/public-groups.php`);
          if (res.data?.success && res.data.groups.length > 0) {
            setGroups(res.data.groups);
            setRegRole(res.data.groups[0].name);
          }
          if (res.data?.registro_config) {
            setRegistroConfig(res.data.registro_config);
          }
          if (res.data?.convenios) {
            setConvenios(res.data.convenios);
          }
        } catch (err) {
          setGroups([{ name: 'estudiante', description: 'Estudiante' }]);
          setRegRole('estudiante');
        } finally {
          setLoadingGroups(false);
        }
      };
      fetchGroups();
    }
  }, [isOpen]);

  // Los checks de aceptacion SIEMPRE arrancan desmarcados: el usuario debe marcarlos.
  useEffect(() => {
    if (!isOpen) setRegChecks({});
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // El aviso de error del ingreso se va solo a los 5 segundos.
  useEffect(() => {
    if (loginAviso?.tipo !== 'error') return undefined;
    const t = setTimeout(() => setLoginAviso(null), 5000);
    return () => clearTimeout(t);
  }, [loginAviso]);

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (verificando) return;
    setLoginError('');
    setLoginAviso(null);
    const errorLogin = tl(cfg.texto_login_error, 'Credenciales inválidas');
    // Con `validar_email` no se consulta al servidor si el correo no tiene forma
    // de correo: se avisa arriba y bajo el campo.
    if (cfg.validar_email && !loginPorDni && !EMAIL_VALIDO.test(loginEmail.trim())) {
      setEmailTocado(true);
      setLoginAviso({ tipo: 'error', texto: tl(cfg.texto_error_email, 'Por favor ingresa un correo electrónico válido.') });
      return;
    }
    setVerificando(true);
    try {
      const sinPwd = !!registroConfig?.login_sin_password;
      // Con `login_por_dni` el campo de arriba es el número de identificación;
      // viaja por el mismo parámetro y el backend resuelve cuál es cuál.
      const response = await authService.login(
        sinPwd
          ? { email: loginEmail.trim(), passwordless: true }
          : { email: loginEmail.trim(), password: loginPassword }
      );
      const { token, user } = response.data;
      const entrar = () => {
        login({ token, user });
        onClose();
        navigate(destinoTrasEntrar);
      };
      // Con mensaje de bienvenida, se muestra un momento antes de entrar.
      const exito = tl(cfg.texto_login_exito, '');
      if (exito) {
        setLoginAviso({ tipo: 'exito', texto: exito });
        setTimeout(entrar, 1500);
      } else {
        entrar();
      }
    } catch (err) {
      setVerificando(false);
      // Cada proyecto tiene su propia cuenta: quien solo está en otro debe registrarse aquí.
      const msg = err.response?.data?.code === 'sin_cuenta_proyecto' ? err.response.data.message : errorLogin;
      if (cfg.mensajes_arriba) setLoginAviso({ tipo: 'error', texto: msg });
      else setLoginError(msg);
    }
  };

  // Helpers que soportan tanto el nuevo formato (array) como el viejo (objeto).
  // El array trae [{key, visible, requerido, ancho, etiqueta, es_custom, tipo}].
  // La normalización antepone nombre/email/password cuando la config viene del
  // formato viejo, que solo listaba los opcionales: sin eso el formulario se
  // queda únicamente con esos opcionales (ver utils/registroCampos.js).
  const camposLista = normalizarCampos(registroConfig);
  const buscarCampo = (k) => camposLista.find(c => c.key === k);
  const campoVisible   = (campo) => !!buscarCampo(campo)?.visible;
  // Valor actual de un campo, para resolver condiciones "mostrar solo si...".
  const valorDeCampo = (key) => {
    switch (key) {
      case 'subrol':       return regSubrol;
      case 'especialidad': return regEspecialidad;
      case 'pais':         return regPais;
      // El convenio se guarda por id, pero las condiciones se escriben con el
      // NOMBRE (es lo que se ve en el admin). Se devuelven los dos, separados
      // por coma, porque `cumpleCondicion` compara contra cada parte: así valen
      // tanto las condiciones nuevas («Hospital La María») como las que algún
      // proyecto hubiera escrito con el identificador.
      case 'convenio': {
        const cv = convenios.find(x => String(x.id) === String(regConvenioId));
        return cv ? `${cv.nombre}, ${cv.id}` : regConvenioId;
      }
      case 'nombre':       return regNombre;
      case 'apellido':     return regApellido;
      case 'dni':          return regDni;
      default:             return regCustomValues[key] || '';
    }
  };
  // Un campo puede depender de otro: `condicion = {campo, valores[]}`. Sin
  // condición (el caso de todos los proyectos anteriores) siempre se cumple.
  // Un campo puede limitarse a ciertos países (`paises: ['CL','MX']`). Vacío o
  // ausente = se pide en todos, que es como funcionan los proyectos actuales.
  const paisPermite = (c) => {
    const lista = Array.isArray(c?.paises) ? c.paises.filter(Boolean) : [];
    if (lista.length === 0) return true;
    const ids = (audiencia?.paisIds || []).map(normalizarPais);
    if (ids.length === 0) return false;
    return lista.some(p => ids.includes(normalizarPais(p)));
  };
  const cumpleCondicion = (c) => {
    if (!paisPermite(c)) return false;
    const cond = c?.condicion;
    if (!cond || !cond.campo || !Array.isArray(cond.valores) || cond.valores.length === 0) return true;
    const actual = String(valorDeCampo(cond.campo) || '');
    // El campo del que depende puede ser de selección múltiple ("a, b")
    const partes = actual.split(',').map(v => v.trim()).filter(Boolean);
    return cond.valores.some(v => partes.includes(String(v)));
  };
  const condicionDeClave = (key) => cumpleCondicion(buscarCampo(key));

  // Campo oculto (`oculto: true`): no se dibuja pero sí viaja en el registro.
  // El perfil (subrol) es el caso típico: sale del país del visitante o del
  // valor por defecto que fijó el admin, sin preguntárselo a nadie. Se resuelve
  // en el momento de armar el payload, no con estado, para que siempre refleje
  // el país que está eligiendo el visitante.
  const campoSubrol = buscarCampo('subrol');
  const valorSubrol = campoSubrol?.oculto
    ? (audiencia?.subrol || campoSubrol.valor_defecto || '')
    : regSubrol;
  const valorCustom = (c) => (c.oculto ? (c.valor_defecto || '') : (regCustomValues[c.key] || ''));
  const campoRequerido = (campo) => !!buscarCampo(campo)?.requerido;
  const campoEtiqueta  = (campo) => buscarCampo(campo)?.etiqueta;
  // Proyectos donde se entra con el número de identificación: ese documento es
  // también la contraseña inicial (el usuario la cambia luego en su perfil).
  const loginPorDni = !!registroConfig?.login_por_dni;

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    if (!regRole && !proyecto?.rol_default) {
      setRegError('Selecciona un grupo de contenido');
      return;
    }
    // Validar campos dinámicos requeridos. Un campo que no se llegó a dibujar
    // (convenio/país/especialidad/perfil sin catálogo cargado) no puede ser
    // obligatorio: si no, el registro queda bloqueado por algo invisible.
    const catalogoSubroles = Array.isArray(proyecto?.config_json?.subroles) ? proyecto.config_json.subroles : [];
    const especialidadDibujada = buscarCampo('especialidad')?.tipo === 'texto'
      || !!registroConfig?.especialidades?.length;
    if (campoRequerido('convenio') && condicionDeClave('convenio') && convenios.length && !regConvenioId) {
      setRegError('Selecciona un convenio'); return;
    }
    if (campoRequerido('telefono') && condicionDeClave('telefono') && !regTelefono.trim()) {
      setRegError('El teléfono es obligatorio'); return;
    }
    if (campoRequerido('especialidad') && condicionDeClave('especialidad') && especialidadDibujada && !regEspecialidad.trim()) {
      setRegError('La especialidad es obligatoria'); return;
    }
    if (campoRequerido('pais') && condicionDeClave('pais') && registroConfig?.paises?.length && !regPais) {
      setRegError('El país es obligatorio'); return;
    }
    if (campoRequerido('dni') && condicionDeClave('dni') && !regDni.trim()) {
      setRegError(`${campoEtiqueta('dni') || 'DNI'} es obligatorio`); return;
    }
    if (campoRequerido('subrol') && !campoSubrol?.oculto && condicionDeClave('subrol') && catalogoSubroles.length && !regSubrol) {
      setRegError(`${campoEtiqueta('subrol') || 'El perfil'} es obligatorio`); return;
    }
    try {
      const sinPwd = !!registroConfig?.login_sin_password;
      const porDni = !!registroConfig?.login_por_dni;
      const separar = !!registroConfig?.separar_nombre_apellido;
      const nombreCompleto = separar
        ? `${regNombre || ''}${regApellido ? ' ' + regApellido : ''}`.trim()
        : (regNombre || '').trim();
      const payload = {
        nombre: nombreCompleto,
        email: regEmail,
        // login_sin_password: contraseña random (nunca se usa, se entra solo con
        // el email). login_por_dni: no se manda nada y el backend asigna el
        // número de identificación como contraseña inicial.
        password: porDni
          ? ''
          : (sinPwd ? Math.random().toString(36).slice(2) + Date.now() : regPassword),
        role: regRole,
      };
      if (separar && regApellido) payload.apellido = regApellido;
      if (campoVisible('convenio') && condicionDeClave('convenio') && regConvenioId) payload.convenio_id = parseInt(regConvenioId);
      if (campoVisible('telefono') && condicionDeClave('telefono') && regTelefono) { payload.telefono = regTelefono; payload.codigo_pais = regCodigoPais; }
      if (campoVisible('especialidad') && condicionDeClave('especialidad') && regEspecialidad.trim()) payload.especialidad = regEspecialidad.trim();
      if (campoVisible('pais') && condicionDeClave('pais') && regPais) payload.pais = regPais;
      if (campoVisible('dni') && condicionDeClave('dni') && regDni) payload.dni = regDni;
      if (campoVisible('subrol') && condicionDeClave('subrol') && valorSubrol) payload.subrol = valorSubrol;
      // Campos personalizados creados desde el editor del proyecto
      const camposCustomEnviar = {};
      const customVisibles = camposLista.filter(c => c.es_custom && c.visible && cumpleCondicion(c));
      customVisibles.forEach(c => {
        const v = String(valorCustom(c)).trim();
        if (v) camposCustomEnviar[c.key] = v;
      });
      // Un campo personalizado marcado como obligatorio se valida acá: los
      // select y las casillas no disparan el `required` nativo del navegador.
      const customFaltante = customVisibles.find(c => {
        if (!c.requerido || c.oculto || camposCustomEnviar[c.key]) return false;
        // Select/casillas sin opciones cargadas no se dibujan: no se exigen.
        if (['select', 'checkbox'].includes(c.tipo)) return !!(Array.isArray(c.opciones) && c.opciones.length);
        return true;
      });
      if (customFaltante) {
        setRegError(`${tl(customFaltante.etiqueta, customFaltante.key)} es obligatorio`);
        return;
      }
      // Validación: checks de aceptación requeridos
      const checks = Array.isArray(registroConfig?.checks) ? registroConfig.checks : [];
      const checksRequeridosFaltantes = checks.filter(c => c.requerido && !regChecks[c.key]);
      if (checksRequeridosFaltantes.length) {
        setRegError(`Falta aceptar: ${checksRequeridosFaltantes.map(c => c.etiqueta).join(', ')}`);
        return;
      }
      // Aceptaciones marcadas → al backend como parte de campos_personalizados
      const acceptances = checks
        .filter(c => regChecks[c.key])
        .map(c => ({ key: c.key, etiqueta: c.etiqueta, accepted_at: new Date().toISOString() }));
      if (acceptances.length) {
        camposCustomEnviar.acceptances = acceptances;
      }
      if (Object.keys(camposCustomEnviar).length) payload.campos_personalizados = camposCustomEnviar;
      const response = await authService.register(payload);
      if (!response || !response.data) throw new Error('Respuesta inválida');
      const { token, user } = response.data;
      // Modo "sin auto-login": no autenticamos, mostramos un mensaje de éxito.
      // El admin lo activa desde AuthModalEditor (cfg.sin_auto_login).
      if (cfg.sin_auto_login === true) {
        setRegSuccess(true);
        return;
      }
      if (!user || user.id === null || user.id === undefined) {
        if (user && user.email) {
          let hash = 0;
          for (let i = 0; i < user.email.length; i++) {
            hash = ((hash << 5) - hash) + user.email.charCodeAt(i);
            hash |= 0;
          }
          login({ token, user: { ...user, id: Math.abs(hash) } });
        } else {
          throw new Error('No se pudo generar ID');
        }
      } else {
        if (typeof user.id === 'string') user.id = parseInt(user.id, 10);
        login({ token, user });
      }
      onClose();
      navigate(cfg.url_destino_registro || destinoTrasEntrar);
    } catch (err) {
      setRegError(err.response?.data?.message || err.message || 'Error al registrar');
    }
  };

  // `color_primario` puede ser un degradado (se usa de fondo en el botón). Para
  // lo que necesita un color plano —enlaces, checks, títulos— se usa este, que
  // ignora los degradados y cae al color de acento configurado.
  const acento = cfg.color_enlace
    || (typeof cfg.color_primario === 'string' && !cfg.color_primario.includes('gradient') ? cfg.color_primario : '')
    || '#0f355b';

  // Encabezado en barra de color: título y subtítulo cambian según la pestaña.
  const barraEncabezado = cfg.encabezado_barra === true;
  const tituloBarra = tab === 'login'
    ? tl(cfg.titulo_login, 'Iniciar Sesión')
    : tl(cfg.titulo_registro, 'Registrarse');
  const subtituloBarra = tab === 'login'
    ? tl(cfg.subtitulo_login, '')
    : tl(cfg.subtitulo_registro, '');

  // Embebido: sin overlay ni click-para-cerrar; el formulario vive en la página.
  const Wrap = embedded ? EmbedWrap : Overlay;
  const wrapProps = embedded ? { $padding: embedPadding } : {
    $overlay: cfg.color_overlay,
    $blur: cfg.blur_overlay,
    onClick: (e) => { if (e.target === e.currentTarget) onClose(); },
  };

  // Estilo común de campos y etiquetas (todo opcional desde el admin).
  const propsInput = {
    $padding: cfg.padding_input,
    $fontSize: cfg.tamano_texto_input,
    $colorTexto: cfg.color_texto_input,
    $colorPlaceholder: cfg.color_placeholder,
    $sombraFocus: cfg.sombra_focus_input,
  };
  const propsLabel = { $size: cfg.tamano_label, $espaciado: cfg.espaciado_label, $margen: cfg.margen_label };
  const bpMovil = cfg.breakpoint_movil || '600px';
  const sinPassword = !!registroConfig?.login_sin_password;
  // Con `avisos_desde_inicio` los avisos se ven desde que abre el modal (como el
  // original) y se van solos en cuanto el correo es válido.
  const avisosDesdeInicio = cfg.avisos_desde_inicio === true;
  const correoMal = !EMAIL_VALIDO.test(loginEmail.trim());
  const emailInvalido = cfg.validar_email === true && !loginPorDni && correoMal
    && (avisosDesdeInicio || (emailTocado && loginEmail.trim() !== ''));
  const verAvisoObligatorios = !!tl(cfg.aviso_obligatorios, '')
    && !(avisosDesdeInicio && tab === 'login' && (!correoMal || loginPorDni));

  return (
    <Wrap {...wrapProps}>
      <ModalCard
        $embedded={embedded}
        $blur={cfg.blur_modal}
        $borde={cfg.borde_modal}
        $sombra={cfg.sombra_modal}
        $bg={cfg.color_fondo_modal}
        $color={cfg.color_texto}
        $font={cfg.fuente}
        $radio={cfg.radio_modal}
        $maxWidth={cfg.ancho_modal}
        $scrollThumb={cfg.color_scroll_thumb}
        $scrollThumbHover={cfg.color_scroll_thumb_hover}
        $scrollTrack={cfg.color_scroll_track}
        $scrollWidth={cfg.ancho_scroll}
        $rebote={cfg.animacion_rebote === true}
      >
        {!embedded && (
          <CloseBtn
            onClick={onClose}
            aria-label="Cerrar"
            $top={barraEncabezado ? (cfg.cerrar_top || '22px') : undefined}
            $right={barraEncabezado ? (cfg.cerrar_right || '22px') : undefined}
            $size={barraEncabezado ? (cfg.cerrar_tamano || '40px') : undefined}
            $fontSize={barraEncabezado ? (cfg.cerrar_tamano_texto || '1.5rem') : undefined}
            $bg={barraEncabezado ? (cfg.cerrar_fondo || 'rgba(255,255,255,0.22)') : undefined}
            $bgHover={barraEncabezado ? (cfg.cerrar_fondo_hover || 'rgba(255,255,255,0.35)') : undefined}
            $color={barraEncabezado ? (cfg.cerrar_color || '#fff') : undefined}
            $borde={cfg.cerrar_borde}
            $girar={cfg.cerrar_girar === true}
            $movil={(cfg.cerrar_tamano_movil || cfg.cerrar_top_movil)
              ? { bp: bpMovil, size: cfg.cerrar_tamano_movil, top: cfg.cerrar_top_movil } : null}
          >&times;</CloseBtn>
        )}

        {barraEncabezado && !regSuccess && (
          <HeaderBar
            $bg={cfg.encabezado_fondo}
            $color={cfg.encabezado_color}
            $padding={cfg.encabezado_padding}
            $paddingMovil={cfg.encabezado_padding_movil}
            $bp={bpMovil}
          >
            {tituloBarra && (
              <HeaderTitle $size={cfg.tamano_titulo} $sizeMobile={cfg.tamano_titulo_movil} $bp={bpMovil}
                $peso={cfg.peso_titulo} $sombra={cfg.sombra_titulo} $fuente={cfg.fuente_titulo}>
                {tituloBarra}
              </HeaderTitle>
            )}
            {subtituloBarra && (
              <HeaderSub $titulo={!!tituloBarra} $size={cfg.tamano_subtitulo}
                $sizeMobile={cfg.tamano_subtitulo_movil} $bp={bpMovil} $opacidad={cfg.opacidad_subtitulo}>
                {subtituloBarra}
              </HeaderSub>
            )}
          </HeaderBar>
        )}

        {!barraEncabezado && cfg.ocultar_encabezado !== true && (
        <ModalHeader>
          <LogoRow>
            {(() => {
              const tamLogo = parseInt(cfg.tamano_logo_modal, 10) || 56;
              // Si tiene logo custom subido al modal → usarlo
              if (cfg.usar_logo_proyecto === false && cfg.logo_url) {
                const url = cfg.logo_url.startsWith('http') ? cfg.logo_url : `${BACKEND_BASE}/${cfg.logo_url}`;
                return <img src={url} alt="" style={{ width: tamLogo, height: 'auto', objectFit: 'contain' }} />;
              }
              // Sino, usar el logo del proyecto si existe
              if (proyecto?.logo) {
                const url = proyecto.logo.startsWith('http') ? proyecto.logo : `${BACKEND_BASE}/${proyecto.logo}`;
                return <img src={url} alt="" style={{ width: tamLogo, height: 'auto', objectFit: 'contain' }} />;
              }
              return <MiniChip />;
            })()}
            {cfg.mostrar_nombre_proyecto !== false && (
              <LogoText $color={cfg.color_texto} $font={cfg.fuente}>
                {cfg.texto_marca || proyecto?.nombre || 'MentorIA'}
              </LogoText>
            )}
          </LogoRow>
        </ModalHeader>
        )}

        {/* Panel de éxito post-registro (cuando sin_auto_login está activo) */}
        {regSuccess && (() => {
          const titulo = tl(cfg.mensaje_post_registro_titulo, '¡Registro exitoso!');
          const detalle = tl(cfg.mensaje_post_registro_detalle, 'Te enviamos un correo de confirmación. Cuando lo recibas, podés iniciar sesión.');
          const ctaTxt = tl(cfg.mensaje_post_registro_cta, 'Entendido');
          return (
            <div style={{
              padding: '1.5rem 2rem 2rem',
              textAlign: 'center',
              animation: 'fadeIn 0.3s ease-out',
            }}>
              <div style={{
                width: 72, height: 72, margin: '0.4rem auto 1rem',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${cfg.color_primario || '#0f355b'}, ${cfg.color_secundario || '#14b6cb'})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 10px 30px ${acento}44`,
              }}>
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ margin: '0 0 0.5rem', color: acento, fontFamily: 'inherit', fontWeight: 700, fontSize: '1.2rem' }}>{titulo}</h3>
              <p style={{ margin: '0 0 1.4rem', color: cfg.color_texto_secundario || '#64748b', fontSize: '0.92rem', lineHeight: 1.5 }}>{detalle}</p>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: cfg.color_primario || '#0f355b',
                  color: cfg.color_texto_boton || '#fff',
                  border: 'none',
                  borderRadius: cfg.radio_botones || '8px',
                  padding: '0.7rem 1.8rem',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >{ctaTxt}</button>
            </div>
          );
        })()}

        {/* Si sólo hay un tab visible, ocultamos toda la fila para una UI más limpia */}
        {!regSuccess && cfg.ocultar_tabs !== true && (tabLogin && tabRegistro) && (
          <TabRow $bg={cfg.color_tab_fondo}>
            {tabLogin && (
              <Tab
                $active={tab === 'login'}
                $bgActive={cfg.color_tab_activo_fondo}
                $colorActive={acento}
                $colorInactive={cfg.color_tab_inactivo}
                onClick={() => { setTab('login'); setLoginError(''); }}
              >
                {tl(cfg.texto_tab_login, 'Iniciar Sesión')}
              </Tab>
            )}
            {tabRegistro && (
              <Tab
                $active={tab === 'register'}
                $bgActive={cfg.color_tab_activo_fondo}
                $colorActive={acento}
                $colorInactive={cfg.color_tab_inactivo}
                onClick={() => { setTab('register'); setRegError(''); }}
              >
                {tl(cfg.texto_tab_registro, 'Registrarse')}
              </Tab>
            )}
          </TabRow>
        )}

        {!regSuccess && (
        <FormBody $padding={cfg.padding_formulario} $fondo={cfg.fondo_formulario} $paddingMovil={cfg.padding_formulario_movil}>
          {/* Aviso fijo de campos obligatorios: el diseño lo muestra siempre,
              arriba del formulario, no solo cuando falla el envío. */}
          {verAvisoObligatorios && (
            <ErrorMsg
              role="note"
              $caja={cfg.error_caja === true}
              $color={cfg.error_color}
              $bg={cfg.error_fondo}
              $borde={cfg.error_borde}
              $padding={cfg.error_padding}
              $margen={cfg.error_margen}
              $tamano={cfg.error_tamano}
            >
              {tl(cfg.aviso_obligatorios, '')}
            </ErrorMsg>
          )}
          {tab === 'login' ? (
            <form onSubmit={handleLogin} noValidate={cfg.validar_email === true}>
              {loginAviso && (
                <AvisoAcceso role={loginAviso.tipo === 'error' ? 'alert' : 'status'} $tipo={loginAviso.tipo}>
                  {loginAviso.texto}
                </AvisoAcceso>
              )}
              <InputGroup>
                <InputLabel $color={cfg.color_texto} {...propsLabel}>
                  {loginPorDni
                    ? (tl(campoEtiqueta('dni'), '') || tl(cfg.label_dni, 'Número de identificación'))
                    : tl(cfg.label_email, 'Correo electrónico')}
                </InputLabel>
                <InputBox>
                  {showIcons && <InputIcon>{loginPorDni ? (cfg.icono_dni || '🪪') : (cfg.icono_email || '📧')}</InputIcon>}
                  <Input
                    type={loginPorDni ? 'text' : 'email'}
                    inputMode={loginPorDni ? 'numeric' : undefined}
                    $hasIcon={showIcons}
                    $bg={cfg.color_fondo_input}
                    $borderColor={cfg.color_borde_input}
                    $borderColorFocus={cfg.color_borde_input_focus}
                    $borderWidth={cfg.ancho_borde_input}
                    $radio={cfg.radio_inputs}
                    {...propsInput}
                    placeholder={ph(loginPorDni
                      ? tl(cfg.placeholder_dni, 'Tu número de identificación')
                      : tl(cfg.placeholder_email, 'tu@email.com'))}
                    value={loginEmail}
                    onChange={e => { setLoginEmail(e.target.value); setEmailTocado(true); }}
                    aria-invalid={emailInvalido || undefined}
                    required
                    autoFocus
                  />
                </InputBox>
                {emailInvalido && (
                  <CampoError $color={cfg.color_error_email}>
                    {tl(cfg.texto_error_email, 'Por favor ingresa un correo electrónico válido.')}
                  </CampoError>
                )}
              </InputGroup>
              {!sinPassword && (
              <InputGroup>
                <InputLabel $color={cfg.color_texto} {...propsLabel}>{tl(cfg.label_password, 'Contraseña')}</InputLabel>
                <InputBox>
                  {showIcons && <InputIcon>{cfg.icono_password || '🔒'}</InputIcon>}
                  <VerContrasena flotante color={cfg.color_texto}>
                    {tipo => (
                      <Input
                        type={tipo}
                        $hasIcon={showIcons}
                        $bg={cfg.color_fondo_input}
                        $borderColor={cfg.color_borde_input}
                        $borderColorFocus={cfg.color_borde_input_focus}
                        $borderWidth={cfg.ancho_borde_input}
                        $radio={cfg.radio_inputs}
                        {...propsInput}
                        placeholder={ph(tl(cfg.placeholder_password, 'Tu contraseña'))}
                        value={loginPassword}
                        onChange={e => setLoginPassword(e.target.value)}
                        required
                      />
                    )}
                  </VerContrasena>
                </InputBox>
              </InputGroup>
              )}
              {loginError && (
                <ErrorMsg $caja={cfg.error_caja === true} $color={cfg.error_color} $bg={cfg.error_fondo} $borde={cfg.error_borde}>
                  {loginError}
                </ErrorMsg>
              )}
              <SubmitBtn
                type="submit"
                $bg={cfg.color_primario}
                $color={cfg.color_texto_boton}
                $radio={cfg.radio_botones}
                $borde={cfg.borde_boton}
                $ancho={cfg.ancho_boton}
                $align={cfg.alineacion_boton}
                $padding={cfg.padding_boton}
                $fontSize={cfg.tamano_texto_boton}
                $peso={cfg.peso_boton}
                $sombra={cfg.sombra_boton}
                $sombraHover={cfg.sombra_boton_hover}
                $margen={cfg.margen_boton}
                $espaciado={cfg.espaciado_boton}
                disabled={verificando}
              >
                {verificando ? tl(cfg.texto_verificando, tl(cfg.texto_boton_login, 'Entrar')) : tl(cfg.texto_boton_login, 'Entrar')}
              </SubmitBtn>
              {/* Enlace de recuperación: por defecto lleva a la recuperación de la
                  plataforma (/recuperar-contrasena, que manda el correo con la identidad
                  del proyecto). El proyecto puede darle otro destino u ocultarlo; en
                  proyectos sin contraseña no tiene sentido. */}
              {cfg.ocultar_enlace_recuperar !== true && (!sinPassword || cfg.recuperar_sin_password === true) && (
                <SwitchText $color={cfg.color_switch_texto || cfg.color_texto_secundario} $linkColor={cfg.color_switch_enlace || acento}>
                  <a
                    href={cfg.enlace_recuperar_url || '/recuperar-contrasena'}
                    style={{ color: cfg.color_switch_texto || cfg.color_texto_secundario || '#64748B', textDecoration: 'none' }}
                  >{tl(cfg.enlace_recuperar_texto, '¿Has olvidado tu contraseña?')}</a>
                </SwitchText>
              )}
              {cfg.ocultar_switch_link !== true && tabRegistro && (
                <SwitchText $color={cfg.color_switch_texto || cfg.color_texto_secundario} $linkColor={cfg.color_switch_enlace || acento}>
                  {tl(cfg.texto_switch_a_registro, '¿No tienes cuenta?')}{' '}
                  <button type="button" onClick={() => setTab('register')}>{tl(cfg.texto_switch_a_registro_link, 'Regístrate aquí')}</button>
                </SwitchText>
              )}
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              {!proyecto?.rol_default && (
              <InputGroup>
                <InputLabel $color={cfg.color_texto} {...propsLabel}>Grupo de contenido</InputLabel>
                <Select
                  value={regRole}
                  onChange={e => setRegRole(e.target.value)}
                  disabled={loadingGroups}
                >
                  {loadingGroups ? (
                    <option value="">Cargando...</option>
                  ) : groups.length === 0 ? (
                    <option value="">No disponible</option>
                  ) : (
                    groups.map(g => (
                      <option key={g.name} value={g.name}>
                        {g.name.charAt(0).toUpperCase() + g.name.slice(1)}
                        {g.description ? ` — ${g.description}` : ''}
                      </option>
                    ))
                  )}
                </Select>
              </InputGroup>
              )}
              {/* Campos dinámicos (array ordenado con ancho 50/100). */}
              {(() => {
                const sinPassword = !!registroConfig?.login_sin_password || loginPorDni;
                const sinEtiquetas = cfg.etiquetas_en_placeholder === true;
                const sinAsterisco = cfg.ocultar_asterisco === true;
                const bloques = camposLista.filter(c => {
                  if (!c.visible) return false;
                  if (c.key === 'password' && sinPassword) return false;
                  if (c.oculto) return false;   // viaja en el payload, no se pinta
                  if (!cumpleCondicion(c)) return false;
                  return true;
                }).map(c => {
                  // `etiquetas_en_placeholder`: el rótulo no va encima del campo
                  // sino dentro (como texto de ejemplo, o como primera opción de
                  // las listas). Sin esa clave, el formulario es el de siempre.
                  const label = (etqDefault) => (sinEtiquetas ? null : (
                    <InputLabel $color={cfg.color_texto} {...propsLabel}>
                      {tl(c.etiqueta, etqDefault)}{c.requerido && !sinAsterisco ? ' *' : ''}
                    </InputLabel>
                  ));
                  // Texto de ejemplo de un campo de escribir. Sin rótulo encima
                  // tampoco se marca el asterisco: el aviso de obligatorios
                  // (`aviso_obligatorios`) es el que lo cuenta, si hace falta.
                  const phCampo = (etqDefault, phDefault) => (sinEtiquetas
                    ? tl(c.etiqueta, etqDefault)
                    : ph(phDefault));
                  // Primera opción (vacía) de una lista desplegable.
                  const opcionVacia = (etqDefault, textoDefault) => (sinEtiquetas
                    ? tl(c.etiqueta, etqDefault)
                    : textoDefault);
                  const baseInputProps = {
                    $hasIcon: showIcons,
                    $bg: cfg.color_fondo_input,
                    $borderColor: cfg.color_borde_input,
                    $borderColorFocus: cfg.color_borde_input_focus,
                    $borderWidth: cfg.ancho_borde_input,
                    $radio: cfg.radio_inputs,
                  };
                  switch (c.key) {
                    case 'nombre':
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label(tl(c.etiqueta, '') || tl(cfg.label_nombre, 'Nombre'))}
                          <InputBox>
                            {showIcons && <InputIcon>{cfg.icono_nombre || '👤'}</InputIcon>}
                            <Input type="text" {...baseInputProps} placeholder={phCampo(tl(cfg.label_nombre, 'Nombre'), tl(cfg.placeholder_nombre, 'Tu nombre'))} value={regNombre} onChange={e => setRegNombre(e.target.value)} required={c.requerido} autoFocus />
                          </InputBox>
                        </InputGroup>
                      )};
                    case 'apellido':
                      return { key: c.key, ancho: c.ancho || 50, jsx: (
                        <InputGroup>
                          {label(tl(c.etiqueta, 'Apellido'))}
                          <InputBox>
                            {showIcons && <InputIcon>{cfg.icono_nombre || '👤'}</InputIcon>}
                            <Input type="text" {...baseInputProps} placeholder={phCampo('Apellido', 'Tu apellido')} value={regApellido} onChange={e => setRegApellido(e.target.value)} required={c.requerido} />
                          </InputBox>
                        </InputGroup>
                      )};
                    case 'email':
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label(tl(c.etiqueta, '') || tl(cfg.label_email, 'Correo electrónico'))}
                          <InputBox>
                            {showIcons && <InputIcon>{cfg.icono_email || '📧'}</InputIcon>}
                            <Input type="email" {...baseInputProps} placeholder={phCampo(tl(cfg.label_email, 'Correo electrónico'), tl(cfg.placeholder_email, 'tu@email.com'))} value={regEmail} onChange={e => setRegEmail(e.target.value)} required={c.requerido} />
                          </InputBox>
                        </InputGroup>
                      )};
                    case 'password':
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label(tl(c.etiqueta, '') || tl(cfg.label_password, 'Contraseña'))}
                          <InputBox>
                            {showIcons && <InputIcon>{cfg.icono_password || '🔒'}</InputIcon>}
                            <VerContrasena flotante color={cfg.color_texto}>
                              {tipo => (
                                <Input type={tipo} {...baseInputProps} placeholder={phCampo(tl(cfg.label_password, 'Contraseña'), tl(cfg.placeholder_password, 'Mínimo 6 caracteres'))} value={regPassword} onChange={e => setRegPassword(e.target.value)} required={c.requerido} />
                              )}
                            </VerContrasena>
                          </InputBox>
                        </InputGroup>
                      )};
                    case 'convenio':
                      if (!convenios.length) return null;
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label('Convenio')}
                          <Select value={regConvenioId} onChange={e => setRegConvenioId(e.target.value)}>
                            <option value="">{opcionVacia('Convenio', 'Selecciona un convenio')}</option>
                            {convenios.map(cv => (
                              <option key={cv.id} value={cv.id}>{cv.nombre}{cv.institucion ? ` — ${cv.institucion}` : ''}</option>
                            ))}
                          </Select>
                        </InputGroup>
                      )};
                    case 'telefono':
                      // `sin_indicativo`: un solo campo de número, sin la lista de indicativos.
                      if (c.sin_indicativo) {
                        return { key: c.key, ancho: c.ancho || 100, jsx: (
                          <InputGroup>
                            {label('Teléfono')}
                            <Input type="tel" {...baseInputProps} placeholder={phCampo('Teléfono', 'Número de teléfono')} value={regTelefono} onChange={e => setRegTelefono(e.target.value)} required={c.requerido} />
                          </InputGroup>
                        )};
                      }
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label('Teléfono')}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Select
                              value={regCodigoPais}
                              onChange={e => setRegCodigoPais(e.target.value)}
                              /* En pantallas estrechas el indicativo cede espacio
                                 para que el número siga siendo legible. */
                              style={{ width: '130px', maxWidth: '42%', flex: '0 0 auto' }}
                              $radio={cfg.radio_inputs}
                    {...propsInput}
                              $bg={cfg.color_fondo_input}
                              $borderColor={cfg.color_borde_input}
                              $borderColorFocus={cfg.color_borde_input_focus}
                              $borderWidth={cfg.ancho_borde_input}
                            >
                              {codigosTelefono.map(o => <option key={o.key} value={o.value}>{o.label}</option>)}
                            </Select>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Input type="tel" {...baseInputProps} placeholder={phCampo('Teléfono', 'Número de teléfono')} value={regTelefono} onChange={e => setRegTelefono(e.target.value)} required={c.requerido} />
                            </div>
                          </div>
                        </InputGroup>
                      )};
                    case 'especialidad': {
                      // El proyecto elige el tipo de control desde el admin:
                      // 'texto' = campo libre, 'checkbox' = casillas (varias
                      // opciones); por defecto, lista desplegable con el
                      // catálogo de especialidades.
                      const lblEsp = tl(c.etiqueta, '') || 'Especialidad';
                      if (c.tipo === 'texto') {
                        return { key: c.key, ancho: c.ancho || 100, jsx: (
                          <InputGroup>
                            {label(lblEsp)}
                            <InputBox>
                              <Input type="text" {...baseInputProps} placeholder={phCampo('Especialidad', 'Escribe tu especialidad')} value={regEspecialidad} onChange={e => setRegEspecialidad(e.target.value)} required={c.requerido} />
                            </InputBox>
                          </InputGroup>
                        )};
                      }
                      if (!(registroConfig?.especialidades?.length)) return null;
                      if (c.tipo === 'checkbox') {
                        return { key: c.key, ancho: c.ancho || 100, jsx: (
                          <InputGroup>
                            {label(lblEsp)}
                            <div style={cajaCasillas(registroConfig.especialidades.length)}>
                              {registroConfig.especialidades.map(e => (
                                <label key={e} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.88rem', color: cfg.color_texto || 'inherit', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={listaValores(regEspecialidad).includes(e)}
                                    onChange={() => setRegEspecialidad(prev => alternarValor(prev, e))}
                                  />
                                  {e}
                                </label>
                              ))}
                            </div>
                          </InputGroup>
                        )};
                      }
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label(lblEsp)}
                          <Select value={regEspecialidad} onChange={e => setRegEspecialidad(e.target.value)}>
                            <option value="">{opcionVacia('Especialidad', 'Selecciona especialidad')}</option>
                            {registroConfig.especialidades.map(e => <option key={e} value={e}>{e}</option>)}
                          </Select>
                        </InputGroup>
                      )};
                    }
                    case 'pais':
                      if (!(registroConfig?.paises?.length)) return null;
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label('País')}
                          <Select value={regPais} onChange={e => setRegPais(e.target.value)}>
                            <option value="">{opcionVacia('País', 'Selecciona país')}</option>
                            {registroConfig.paises.map(p => <option key={p} value={p}>{p}</option>)}
                          </Select>
                        </InputGroup>
                      )};
                    case 'dni':
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label('DNI')}
                          <Input type="text" {...baseInputProps} $hasIcon={false} placeholder={phCampo('DNI', 'Número de documento')} value={regDni} onChange={e => setRegDni(e.target.value)} />
                        </InputGroup>
                      )};
                    case 'subrol': {
                      // Perfil del participante. El catálogo lo define el proyecto
                      // en config_json.subroles; sin catálogo, el campo no se muestra.
                      // Los perfiles marcados `solo_admin` (p. ej. Inactivos o
                      // Graduados) existen para clasificar desde el panel, no
                      // para que nadie los elija al registrarse.
                      const catalogo = (Array.isArray(proyecto?.config_json?.subroles) ? proyecto.config_json.subroles : [])
                        .filter(s => s.solo_admin !== true);
                      if (!catalogo.length) return null;
                      return { key: c.key, ancho: c.ancho || 100, jsx: (
                        <InputGroup>
                          {label('Perfil')}
                          <Select value={regSubrol} onChange={e => setRegSubrol(e.target.value)}>
                            <option value="">{opcionVacia('Perfil', 'Selecciona tu perfil')}</option>
                            {catalogo.map(s => <option key={s.key} value={s.key}>{s.label || s.key}</option>)}
                          </Select>
                        </InputGroup>
                      )};
                    }
                    default:
                      if (c.es_custom) {
                        const lbl = tl(c.etiqueta, c.key);
                        const opciones = Array.isArray(c.opciones) ? c.opciones : [];
                        // Select y casillas se alimentan de las opciones cargadas
                        // en el admin; sin opciones el campo no se muestra.
                        if (c.tipo === 'select') {
                          if (!opciones.length) return null;
                          return { key: c.key, ancho: c.ancho || 100, jsx: (
                            <InputGroup>
                              {label(lbl)}
                              <Select
                                value={regCustomValues[c.key] || ''}
                                onChange={e => setRegCustomValues(prev => ({ ...prev, [c.key]: e.target.value }))}
                              >
                                <option value="">{opcionVacia(c.key, 'Selecciona una opción')}</option>
                                {opciones.map(o => <option key={o} value={o}>{o}</option>)}
                              </Select>
                            </InputGroup>
                          )};
                        }
                        // Aceptación: una sola casilla, con enlace opcional a
                        // los términos. Guarda "Sí" / vacío.
                        if (c.tipo === 'aceptacion') {
                          const aceptado = !!regCustomValues[c.key];
                          return { key: c.key, ancho: c.ancho || 100, jsx: (
                            <InputGroup>
                              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.55rem', fontSize: '0.9rem', color: cfg.color_texto || 'inherit', cursor: 'pointer', lineHeight: 1.4 }}>
                                <input
                                  type="checkbox"
                                  checked={aceptado}
                                  onChange={e => setRegCustomValues(prev => ({ ...prev, [c.key]: e.target.checked ? (c.valor_defecto || 'Sí') : '' }))}
                                  style={{ marginTop: '0.15rem' }}
                                />
                                <span>
                                  {lbl}{' '}
                                  {c.enlace_url && (
                                    <a
                                      href={c.enlace_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={e => e.stopPropagation()}
                                      style={{ color: acento }}
                                    >{tl(c.enlace_texto, 'términos y condiciones')}</a>
                                  )}
                                  {c.requerido && !sinAsterisco ? ' *' : ''}
                                </span>
                              </label>
                            </InputGroup>
                          )};
                        }
                        if (c.tipo === 'checkbox') {
                          if (!opciones.length) return null;
                          return { key: c.key, ancho: c.ancho || 100, jsx: (
                            <InputGroup>
                              {label(lbl)}
                              <div style={cajaCasillas(opciones.length)}>
                                {opciones.map(o => (
                                  <label key={o} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.88rem', color: cfg.color_texto || 'inherit', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={listaValores(regCustomValues[c.key]).includes(o)}
                                      onChange={() => setRegCustomValues(prev => ({ ...prev, [c.key]: alternarValor(prev[c.key], o) }))}
                                    />
                                    {o}
                                  </label>
                                ))}
                              </div>
                            </InputGroup>
                          )};
                        }
                        return { key: c.key, ancho: c.ancho || 100, jsx: (
                          <InputGroup>
                            {label(lbl)}
                            <Input
                              {...baseInputProps}
                              $hasIcon={false}
                              {...(c.tipo === 'textarea' ? { as: 'textarea', rows: 4, style: { resize: 'vertical', minHeight: 90 } } : { type: c.tipo || 'text' })}
                              placeholder={phCampo(c.key, lbl)}
                              value={regCustomValues[c.key] || ''}
                              onChange={e => setRegCustomValues(prev => ({ ...prev, [c.key]: e.target.value }))}
                            />
                          </InputGroup>
                        )};
                      }
                      return null;
                  }
                }).filter(Boolean);
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 0.75rem' }}>
                    {bloques.map(b => (
                      <div
                        key={b.key}
                        style={{
                          flex: b.ancho === 50 ? '1 1 calc(50% - 0.4rem)' : '1 1 100%',
                          // `ancho_minimo_campo`: por debajo de ese ancho los campos a media
                          // columna pasan a ocupar la fila entera (celular).
                          minWidth: b.ancho === 50 && cfg.ancho_minimo_campo ? `min(100%, ${cfg.ancho_minimo_campo})` : 0,
                        }}
                      >
                        {b.jsx}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Cuadro de políticas según país seleccionado */}
              {(() => {
                const polDef = registroConfig?.politicas_por_pais?.default || '';
                const polPais = registroConfig?.politicas_por_pais?.[regPais] || polDef;
                if (!polPais) return null;
                return (
                  <div style={{ marginTop: '0.5rem', marginBottom: '0.8rem', padding: '0.7rem 0.9rem', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, maxHeight: 150, overflowY: 'auto', fontSize: '0.72rem', color: '#475569', lineHeight: 1.45 }}
                       dangerouslySetInnerHTML={{ __html: polPais }} />
                );
              })()}

              {/* Checkboxes de aceptación */}
              {Array.isArray(registroConfig?.checks) && registroConfig.checks.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                  {registroConfig.checks.map(chk => {
                    const variante = registroConfig.checks_variantes_pais?.[regPais]?.[chk.key] || {};
                    const etiqueta = tl(variante.etiqueta, '') || tl(chk.etiqueta, chk.key);
                    const linkUrl  = variante.link_url || chk.link_url;
                    const linkTxt  = tl(chk.link_texto, 'leer');
                    return (
                      <label key={chk.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: cfg.color_texto || '#334155', lineHeight: 1.35 }}>
                        <input
                          type="checkbox"
                          checked={!!regChecks[chk.key]}
                          onChange={e => setRegChecks(prev => ({ ...prev, [chk.key]: e.target.checked }))}
                          style={{
                            marginTop: 3,
                            accentColor: cfg.color_check || acento,
                            width: cfg.tamano_check || 16,
                            height: cfg.tamano_check || 16,
                          }}
                        />
                        <span>
                          {etiqueta}{chk.requerido && cfg.ocultar_asterisco !== true ? ' *' : ''}
                          {linkUrl && (
                            <> · <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: cfg.color_secundario || '#0891b2', textDecoration: 'underline' }}>{linkTxt}</a></>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {regError && (
                <ErrorMsg $caja={cfg.error_caja === true} $color={cfg.error_color} $bg={cfg.error_fondo} $borde={cfg.error_borde}>
                  {regError}
                </ErrorMsg>
              )}
              <SubmitBtn
                type="submit"
                disabled={loadingGroups || (!regRole && !proyecto?.rol_default)}
                $bg={cfg.color_primario}
                $color={cfg.color_texto_boton}
                $radio={cfg.radio_botones}
                $borde={cfg.borde_boton}
                $ancho={cfg.ancho_boton}
                $align={cfg.alineacion_boton}
                $padding={cfg.padding_boton}
              >
                {tl(cfg.texto_boton_registro, 'Crear cuenta')}
              </SubmitBtn>
              {cfg.ocultar_switch_link !== true && tabLogin && (
                <SwitchText $color={cfg.color_switch_texto || cfg.color_texto_secundario} $linkColor={cfg.color_switch_enlace || acento}>
                  {tl(cfg.texto_switch_a_login, '¿Ya tienes cuenta?')}{' '}
                  <button type="button" onClick={() => setTab('login')}>{tl(cfg.texto_switch_a_login_link, 'Inicia sesión')}</button>
                </SwitchText>
              )}
            </form>
          )}
        </FormBody>
        )}
      </ModalCard>
    </Wrap>
  );
};

export default AuthModal;
